import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiFieldNumber, EuiFieldText, EuiFormRow, EuiRadio, EuiSpacer, EuiSwitch, EuiTab, EuiTabs, EuiText } from '@elastic/eui'

import { apiPutOrganization } from '../../api/adcritterApi'
import { OrganizationReserveSettings } from '../../api/interfaces'
import { AgencyReserveType } from '../../api/interfaces/ReserveSettings'
import { useSetOrganizationAgencyReserveSettingsMutation } from '../../api/rtkQueryApi/platform/organizationsApi'
import { showSuccessToast, updateOrganization, updateOrganizationState } from '../../app/appSlice'
import { RootState } from '../../app/rootReducer'
import { useDebounceEffect } from '../../utils/useDebounceEffect'
import { CampaignsAgencySettings } from './CampaignsAgencySettings'
import { ClientsAgencySettings } from './ClientsAgencySettings'

enum AgencySettingsTabs {
  Clients,
  Campaigns
}

export const AgencySettingsDrawer: React.FC = () => {
  const { currentOrganization } = useSelector((state: RootState) => state.app)
  const [updatedName, setUpdatedName] = React.useState<string>('')
  const [updatedBillingEmail, setUpdatedBillingEmail] = React.useState<string>('')
  const dispatch = useDispatch()
  const [setOrganizationAgencyReserveSettings] = useSetOrganizationAgencyReserveSettingsMutation()
  const [organizationAgencySettings, setOrganizationAgencySettingsState] = React.useState<OrganizationReserveSettings>()
  const [selectedTab, setSelectedTab] = React.useState(AgencySettingsTabs.Clients)
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)

  useEffect(() => {
    if (currentOrganization) {
      setOrganizationAgencySettingsState({
        reserveRate: currentOrganization.organizationReserveSettings?.reserveRate ?? 0,
        agencyReserveType: currentOrganization.organizationReserveSettings?.agencyReserveType ?? AgencyReserveType.AgencyPurchaser
      })
      setUpdatedName(currentOrganization.name)
      setUpdatedBillingEmail(currentOrganization.billingEmail || '')
    }
  }, [currentOrganization])

  const onSaveOrganizationDetails = () => {
    if (!isValidEmail(updatedBillingEmail)) {
      return
    }
    setIsSaving(true)
    apiPutOrganization(updatedName, updatedBillingEmail)
      .then(response => {
        dispatch(showSuccessToast(`Saved organization`))
        dispatch(updateOrganizationState(response.data))
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  useDebounceEffect(
    1400,
    () => {
      if (currentOrganization && organizationAgencySettings && (currentOrganization.organizationReserveSettings?.reserveRate !== organizationAgencySettings.reserveRate || currentOrganization.organizationReserveSettings?.agencyReserveType !== organizationAgencySettings?.agencyReserveType)) {
        setOrganizationAgencyReserveSettings({
          reserveRate: organizationAgencySettings.reserveRate,
          agencyReserveType: organizationAgencySettings.agencyReserveType
        })
          .unwrap()
          .then(response => {
            if (response) {
              dispatch(updateOrganization(response))
            }
          })
      }
    },
    [organizationAgencySettings, currentOrganization]
  )

  const onOrganizationAgencyReserveSettingsRateChange = (newRate: number) => {
    let rate = newRate / 100
    if (rate > 0.2) {
      rate = 0.2
    } else if (rate < 0) {
      rate = 0
    }
    setOrganizationAgencySettingsState(oldVal => ({
      reserveRate: rate,
      agencyReserveType: oldVal?.agencyReserveType ?? AgencyReserveType.None
    }))
  }

  const onOrganizationAgencyReserveSettingsPurchaserChange = (reserveType: AgencyReserveType) => {
    setOrganizationAgencySettingsState(oldVal => ({
      reserveRate: oldVal?.reserveRate ?? 0,
      agencyReserveType: reserveType
    }))
  }

  function isValidEmail(email: string) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  const hasCustomReserveRate = (currentOrganization?.organizationReserveSettings?.reserveRate ?? 0) > 0.2

  return (
    <React.Fragment>
      <EuiFormRow label='Billing Organization Name'>
        <EuiFieldText name='name' value={updatedName} onChange={e => setUpdatedName(e.target.value)} placeholder='' fullWidth />
      </EuiFormRow>
      <EuiFormRow label='Billing Email' isInvalid={!isValidEmail(updatedBillingEmail)}>
        <EuiFieldText name='billingEmail' value={updatedBillingEmail} onChange={e => setUpdatedBillingEmail(e.target.value)} placeholder='' fullWidth />
      </EuiFormRow>
      <EuiFormRow>
        <EuiButton fill color={'primary'} onClick={onSaveOrganizationDetails} isDisabled={!isValidEmail(updatedBillingEmail)} isLoading={isSaving}>
          Save
        </EuiButton>
      </EuiFormRow>
      <EuiSpacer />
      <EuiFormRow label={'Campaign Billing'}>
        <>
          <EuiRadio
            id={'orgIsAgencyPurchaser'}
            checked={organizationAgencySettings?.agencyReserveType === AgencyReserveType.AgencyPurchaser}
            label={'Bill Agency'}
            onChange={() => {
              onOrganizationAgencyReserveSettingsPurchaserChange(AgencyReserveType.AgencyPurchaser)
            }}
          />
          <EuiText size={'xs'} color={'subdued'}>
            <p>Campaigns are charged to the Agency's payment method</p>
          </EuiText>
          <EuiSpacer size={'s'} />
          <EuiRadio
            id={'orgIsCustomerPurchaser'}
            checked={organizationAgencySettings?.agencyReserveType === AgencyReserveType.CustomerPurchaser}
            label={'Bill Client'}
            onChange={() => {
              onOrganizationAgencyReserveSettingsPurchaserChange(AgencyReserveType.CustomerPurchaser)
            }}
          />
          <EuiText size={'xs'} color={'subdued'}>
            <p>Campaigns are charged to the associated client's payment method</p>
          </EuiText>
        </>
      </EuiFormRow>
      {organizationAgencySettings?.agencyReserveType !== AgencyReserveType.None && (
        <>
          <EuiFormRow label={'Agency Reserve Rate'}>
            <EuiFieldNumber value={((organizationAgencySettings?.reserveRate ?? 0) * 100).toFixed()} append={'%'} step={1} min={0} disabled={hasCustomReserveRate} onChange={e => onOrganizationAgencyReserveSettingsRateChange(Number(e.target.value))} />
          </EuiFormRow>
          {hasCustomReserveRate && (
            <>
              <EuiSpacer size={'s'} />
              <EuiCallOut size={'s'} color={'primary'} iconType={'lock'} title={'Custom reserve enabled'}>
                <p>Your account has a custom agency reserve rate. Please contact AdCritter if you need to change it.</p>
              </EuiCallOut>
            </>
          )}
          <EuiSpacer />
          <EuiFormRow>
            <EuiSwitch label={'Show Advanced Settings'} checked={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)} />
          </EuiFormRow>
          {showAdvanced && (
            <>
              <EuiSpacer />
              <EuiTabs>
                <EuiTab key={AgencySettingsTabs.Clients} isSelected={selectedTab === AgencySettingsTabs.Clients} onClick={() => setSelectedTab(AgencySettingsTabs.Clients)}>
                  Clients
                </EuiTab>
                <EuiTab key={AgencySettingsTabs.Campaigns} isSelected={selectedTab === AgencySettingsTabs.Campaigns} onClick={() => setSelectedTab(AgencySettingsTabs.Campaigns)}>
                  Campaigns
                </EuiTab>
              </EuiTabs>
              <EuiSpacer />
              {selectedTab === AgencySettingsTabs.Clients && <ClientsAgencySettings />}
              {selectedTab === AgencySettingsTabs.Campaigns && <CampaignsAgencySettings />}
            </>
          )}
        </>
      )}
    </React.Fragment>
  )
}
